import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useGetDailyReports } from "../ReportComponents";
import { useGetRangeOfDailyReports } from "../ReportComponents/useReportQueries";
import { usePDF, pdf, PDFDownloadLink } from "@react-pdf/renderer";
import getReportTable, {
  ReportTable,
} from "../ReportComponents/ViewReport/ReportTable";
import { downloadFile, todayDate } from "../../utils/utils";
import { EmptyPaper, LoadingError, LoadingPaper } from "../LoadingComponents";
import ButtonDatePicker from "../ButtonDatePicker/ButtonDatePicker";
import { useAuthUser } from "react-auth-kit";
import { useGetOrganizationById } from "../HandlingComponents";

const DailyReportExport = ({ open, handleConfirm, handleCancel }) => {
  const [exportType, setExportType] = useState("pdf");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const reportsQuery = useGetRangeOfDailyReports(startDate, endDate);
  const [downloadTime, setDownloadTime] = useState(false);

  const fullExport = useRef([]);
  const days = useRef([]);
  const [logoData, setLogoData] = useState({
    includeH3K: true,
    logo: "",
    clientLogoPos: "DX",
  });
  const [orgData, setOrgData] = useState({
    org_name: "",
    address: "",
    cap: "",
    city: "",
  });

  // set logo info
  const authUser = useAuthUser();

  const organizationQuery = useGetOrganizationById(authUser().user.org_id);
  useEffect(() => {
    if (authUser().user.org_id != null && organizationQuery.isSuccess) {
      setLogoData({
        clientLogoPos: organizationQuery.data.client_logo_pos,
        logo: organizationQuery.data.logo,
        includeH3K: organizationQuery.data.include_h3k_logo,
      });
      setOrgData({
        org_name: organizationQuery.data.name,
        address: organizationQuery.data.address,
        cap: organizationQuery.data.cap,
        city: organizationQuery.data.city,
      });
    }
  }, [organizationQuery.data]);

  const generatingReports = useRef(false);
  const downloadingReports = useRef(false);

  const Report = (
    <ReportTable
      data={fullExport.current}
      days={days.current}
      logoInfo={logoData}
      orgInfo={orgData}
    />
  );
  const [instance, updateInstance] = usePDF({ document: Report });

  const updateStartDate = async (date) => {
    // set time to 00:00:00
    const _date = date.set("hour", 0).set("minute", 0).set("second", 0);
    setStartDate(_date);

    fullExport.current.length = 0;
    days.current.length = 0;
    generatingReports.current = false;
    setDownloadTime(false);
  };

  const updateEndDate = async (date) => {
    // set time to 23:59:59
    const _date = date.set("hour", 23).set("minute", 59).set("second", 59);
    setEndDate(_date);

    fullExport.current.length = 0;
    days.current.length = 0;
    generatingReports.current = false;
    setDownloadTime(false);
  };

  const handleChangeExportType = (event) => {
    setExportType(event.target.value);
  };

  const downloadCsv = () => {
    // Headers for each column
    let headers = Object.keys(fullExport.current[0]).join(",");

    // Convert users data to a csv
    let csv = fullExport.current.reduce((acc, patient) => {
      let values = Object.values(patient);
      acc.push([...values].join(","));
      return acc;
    }, []);

    downloadFile({
      data: [headers, ...csv].join("\n"),
      fileName: "daily_report.csv",
      fileType: "text/csv",
    });
  };

  const downloadPdf = () => {
    downloadFile({
      data: instance.blob,
      fileName: "daily_report.pdf",
      fileType: "application/pdf",
    });
  };

  const generateDR = () => {
    generatingReports.current = true;

    try {
      let _fullExport = [];
      let _days = [];

      for (let day of reportsQuery.data) {
        // keep track of days for the table
        _days.push(day[0].recap.date);

        const reports = day.map((patient) => {
          return {
            id: patient.patient_id ?? null,
            org_id: patient.org_id ?? null,
            first_name: patient.first_name ?? null,
            last_name: patient.last_name ?? null,
            isOutOfBounds: patient.isOutOfBounds ?? null,
            alert: patient.alert ?? null,
            date: patient.recap.date ?? null,
            HR: patient.recap.data.HR ?? null,
            SpO2: patient.recap.data.SPO2 ?? null,
            Temperature: patient.recap.data.Temperature ?? null,
            Pmax: patient.recap.data.Pmax ?? null,
            Pmin: patient.recap.data.Pmin ?? null,
            HR_ofb: patient.recap.outOfBounds.HR ?? null,
            SpO2_ofb: patient.recap.outOfBounds.SPO2 ?? null,
            Temperature_ofb: patient.recap.outOfBounds.Temperature ?? null,
            Pmax_ofb: patient.recap.outOfBounds.Pmax ?? null,
            Pmin_ofb: patient.recap.outOfBounds.Pmin ?? null,
            cr_alert: patient.recap.thresholds.cr_alert ?? null,
            email_alert: patient.recap.thresholds.email_alert ?? null,
            gluc_max: patient.recap.thresholds.gluc_max ?? null,
            gluc_min: patient.recap.thresholds.gluc_min ?? null,
            press_dia_max: patient.recap.thresholds.press_dia_max ?? null,
            press_dia_min: patient.recap.thresholds.press_dia_min ?? null,
            press_sis_max: patient.recap.thresholds.press_sis_max ?? null,
            press_sis_min: patient.recap.thresholds.press_sis_min ?? null,
            pulse_max: patient.recap.thresholds.pulse_max ?? null,
            pulse_min: patient.recap.thresholds.pulse_min ?? null,
            sms_alert: patient.recap.thresholds.sms_alert ?? null,
            spO2_max: patient.recap.thresholds.spO2_max ?? null,
            spO2_min: patient.recap.thresholds.spO2_min ?? null,
            temp_max: patient.recap.thresholds.temp_max ?? null,
            temp_min: patient.recap.thresholds.temp_min ?? null,
          };
        });
        _fullExport.push(...reports);
      }

      fullExport.current = _fullExport;
      days.current = _days;

      let allDays = [];
      let currentDate = dayjs(startDate);

      while (currentDate.isBefore(endDate) || currentDate.isSame(endDate)) {
        allDays.push(currentDate.format());
        currentDate = currentDate.add(1, "day");
      }

      updateInstance(
        <ReportTable
          logoInfo={logoData}
          orgInfo={orgData}
          days={allDays}
          data={fullExport.current}
        />
      );
      console.log(`instance:`);
      console.log(instance);
      console.log(logoData);
      generatingReports.current = false;
      setDownloadTime(true);
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <Dialog open={open} onClose={handleCancel} aria-labelledby="export-dialog">
      <DialogTitle id="export-dialog">Esporta Daily Report</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Seleziona il formato di esportazione e l'intervallo di date
        </DialogContentText>
        <FormControl component="fieldset">
          <RadioGroup
            aria-label="exportType"
            name="exportType"
            value={exportType}
            onChange={handleChangeExportType}
          >
            <FormControlLabel value="pdf" control={<Radio />} label="PDF" />
            <FormControlLabel value="csv" control={<Radio />} label="CSV" />
          </RadioGroup>
        </FormControl>
        <Box
          sx={{
            paddingTop: 2,
            alignItems: "center",
          }}
        >
          <Typography variant="caption" gutterBottom>
            Seleziona intervallo di date
          </Typography>
          <Stack direction="row" spacing={2}>
            <ButtonDatePicker
              id="startDate"
              label="Start Date"
              value={startDate}
              onChange={(date) => updateStartDate(date)}
            />
            <ButtonDatePicker
              id="endDate"
              label="End Date"
              value={endDate}
              onChange={(date) => updateEndDate(date)}
            />
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} color="primary">
          Annulla
        </Button>
        {downloadTime && exportType == "csv" && (
          <Button
            disabled={reportsQuery.status === "loading"}
            color="error"
            onClick={downloadCsv}
          >
            Download CSV
          </Button>
        )}
        {downloadTime && exportType == "pdf" && (
          <Button
            color="error"
            disabled={
              instance.loading ||
              instance.error ||
              reportsQuery.status === "loading"
            }
            onClick={downloadPdf}
          >
            Download PDF
          </Button>
        )}
        {!downloadTime && (generatingReports.current || instance.loading) ? (
          <CircularProgress />
        ) : (
          !downloadTime && (
            <Button
              onClick={generateDR}
              disabled={
                startDate == null || endDate == null || reportsQuery.isLoading
              }
            >
              {reportsQuery.isLoading ? "Attendere..." : "Genera report"}
            </Button>
          )
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DailyReportExport;
